(() => {
  const roots = Array.from(document.querySelectorAll('[data-lt-bundle]'));
  if (!roots.length) return;

  const formatMoney = cents => {
    const currency = window.throwbackTheme?.currency || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  };

  roots.forEach(root => {
    if (root.dataset.bundleInitialized === 'true') return;
    root.dataset.bundleInitialized = 'true';

    const required = Number(root.dataset.requiredQuantity || 5);
    const bundlePrice = Number(root.dataset.bundlePrice || 19996);
    const comparePrice = Number(root.dataset.comparePrice || 24995);
    const addEnabled = root.dataset.addEnabled === 'true';
    const cards = Array.from(root.querySelectorAll('[data-bundle-card]'));
    const slots = Array.from(root.querySelectorAll('[data-bundle-slot]'));
    const addButton = root.querySelector('[data-bundle-add]');
    const message = root.querySelector('[data-bundle-message]');
    const progressbar = root.querySelector('[data-bundle-progressbar]');
    const progressCopy = root.querySelector('[data-bundle-progress-copy]');
    const extra = root.querySelector('[data-bundle-extra]');
    const regularTotalEl = root.querySelector('[data-bundle-regular-total]');
    const offerTotalEl = root.querySelector('[data-bundle-offer-total]');
    const counts = new Map();
    // LTs already in the cart count toward the Tacklebox (the discount applies
    // across the whole cart), so the builder starts from them, not from zero.
    let cartItems = [];
    let notice = null; // { text, tone } shown after add-to-cart until the selection changes

    const itemFor = card => ({
      id: Number(card.dataset.variantId),
      price: Number(card.dataset.price || 0),
      available: card.dataset.available === 'true',
      title: card.dataset.title || '',
      image: card.dataset.image || ''
    });

    const totalSelected = () => Array.from(counts.values()).reduce((sum, qty) => sum + qty, 0);

    function syncCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const byId = new Map(cards.map(card => [Number(card.dataset.variantId), card]));
      const next = [];
      cart.items.forEach(line => {
        const card = byId.get(Number(line.variant_id));
        if (!card) return;
        const item = { ...itemFor(card), available: true, inCart: true };
        for (let i = 0; i < Number(line.quantity || 0); i += 1) next.push(item);
      });
      cartItems = next;
      update();
    }

    async function loadCart() {
      try {
        const response = await fetch(`${window.throwbackTheme?.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) syncCart(await response.json());
      } catch (error) { /* builder still works from zero */ }
    }

    function setCount(card, next) {
      const item = itemFor(card);
      if (!item.id) return;
      if (!item.available && next > (counts.get(item.id) || 0)) return;

      notice = null;
      const safeNext = Math.max(0, next);
      counts.set(item.id, safeNext);
      if (safeNext === 0) counts.delete(item.id);
      update();
    }

    function update() {
      const selected = totalSelected();
      const inCart = cartItems.length;
      const total = inCart + selected;
      const expanded = [...cartItems];

      cards.forEach(card => {
        const item = itemFor(card);
        const qty = counts.get(item.id) || 0;
        const output = card.querySelector('[data-bundle-qty]');
        const badge = card.querySelector('[data-bundle-selected-badge]');

        if (output) output.textContent = `${qty}x`;
        card.classList.toggle('is-selected', qty > 0);

        if (badge) {
          badge.hidden = qty <= 0;
          badge.textContent = qty > 0 ? `${qty}x` : '';
        }

        const minus = card.querySelector('[data-bundle-change="-1"]');
        const plus = card.querySelector('[data-bundle-change="1"]');

        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = !item.available;

        for (let i = 0; i < qty; i += 1) expanded.push(item);
      });

      const summaryCount = root.querySelector('[data-bundle-summary-count]');
      const nextEl = root.querySelector('[data-bundle-next]');
      const progress = root.querySelector('[data-bundle-progress]');
      const remaining = Math.max(0, required - total);
      const extras = Math.max(0, total - required);
      const regularTotal = expanded.reduce((sum, item) => sum + item.price, 0);
      // Every Nth bait is free (Shopify Buy X Get Y gives away the lowest-priced ones).
      const freeCount = Math.floor(total / required);
      const bundleSavings = expanded.map(item => item.price).sort((a, b) => a - b).slice(0, freeCount).reduce((sum, p) => sum + p, 0);
      const toNextFree = required - (total % required);
      const offerTotal = total >= required ? Math.max(0, regularTotal - bundleSavings) : bundlePrice;

      if (progressCopy) {
        const cartNote = inCart ? ` · ${inCart} in cart` : '';
        progressCopy.textContent = total < required
          ? `${total} / ${required} selected${cartNote}`
          : `${total} selected · ${freeCount} free${cartNote}`;
      }

      if (summaryCount) {
        summaryCount.textContent = total === 1 ? '1 bait selected' : `${total} baits selected`;
      }

      const inSet = total === 0 ? 0 : ((total - 1) % required) + 1; // progress toward the current free bait
      if (progress) progress.style.width = `${(inSet / required) * 100}%`;
      if (progressbar) {
        progressbar.setAttribute('aria-valuenow', String(Math.min(total, required)));
        progressbar.setAttribute(
          'aria-valuetext',
          total < required
            ? `${total} of ${required} baits selected`
            : `${total} baits selected; ${freeCount} free`
        );
      }

      if (nextEl) {
        nextEl.textContent = (inCart && !selected && remaining)
          ? `${inCart} LT${inCart === 1 ? '' : 's'} already in your cart. ${remaining === 1 ? 'Add 1 more. It\'s free.' : `Add ${remaining} more. The ${required}th one is free.`}`
          : remaining
          ? (total === 0
            ? `Pick ${required}. The ${required}th one is free.`
            : remaining === 1 ? 'Add 1 more. It\'s free.' : `${remaining} more. The ${required}th one is free.`)
          : toNextFree === required
            ? `Tacklebox ready: ${freeCount} free ${freeCount === 1 ? 'bait' : 'baits'}.`
            : `${freeCount} free so far. Add ${toNextFree} more for another free one.`;
      }

      if (extra) {
        extra.hidden = true; // progress line above already covers stacking
        extra.textContent = extras
          ? `Every ${required}th bait is free. ${toNextFree === required ? 'Nice round number.' : `${toNextFree} more for the next free one.`}`
          : '';
      }

      // Tray shows the latest set of N; earlier full sets collapse into "+N".
      const setStart = total > required ? Math.floor((total - 1) / required) * required : 0;
      const trayItems = expanded.slice(setStart, setStart + required);
      const overflow = root.querySelector('[data-bundle-overflow]');
      if (overflow) {
        overflow.hidden = setStart === 0;
        overflow.textContent = setStart ? `+${setStart}` : '';
        overflow.setAttribute('aria-label', `${setStart} more baits selected`);
      }

      slots.forEach((slot, index) => {
        const item = trayItems[index];
        const img = slot.querySelector('img');
        const remove = slot.querySelector('[data-bundle-remove]');

        slot.classList.toggle('is-filled', Boolean(item));
        slot.classList.toggle('is-in-cart', Boolean(item?.inCart));
        slot.dataset.variantId = item && !item.inCart ? String(item.id) : '';
        slot.title = item?.inCart ? `${item.title} (in your cart)` : '';
        if (remove) {
          remove.hidden = !item || Boolean(item.inCart);
          remove.setAttribute('aria-label', item ? `Remove ${item.title}` : 'Remove');
        }

        if (img) {
          if (item?.image) {
            img.src = item.image;
            img.alt = item.title;
            img.hidden = false;
          } else {
            img.removeAttribute('src');
            img.alt = '';
            img.hidden = true;
          }
        }
      });

      if (regularTotalEl) {
        regularTotalEl.textContent = total >= required ? formatMoney(regularTotal) : formatMoney(comparePrice);
      }

      if (offerTotalEl) {
        offerTotalEl.textContent = total >= required ? formatMoney(offerTotal) : formatMoney(bundlePrice);
      }

      if (!addButton) return;

      const unlocked = total >= required && selected > 0;
      const hasUnavailable = expanded.some(item => !item.available);

      if (!unlocked) {
        addButton.disabled = true;
        addButton.textContent = `Choose ${remaining || toNextFree} more`;
      } else if (!addEnabled) {
        addButton.disabled = true;
        addButton.textContent = `${total}-bait preview ready`;
      } else if (hasUnavailable) {
        addButton.disabled = true;
        addButton.textContent = 'One or more colors sold out';
      } else {
        addButton.disabled = false;
        addButton.textContent = inCart
          ? `Add ${selected} ${selected === 1 ? 'bait' : 'baits'} · ${formatMoney(offerTotal)} total`
          : `Add ${total} baits · ${formatMoney(offerTotal)}`;
      }

      if (message) {
        if (notice) {
          message.textContent = notice.text;
          message.dataset.tone = notice.tone;
        } else {
          delete message.dataset.tone;
        }
      }
      if (message && !notice) {
        if (!unlocked) {
          message.textContent = '';
        } else if (!addEnabled) {
          message.textContent = `Preview complete. ${formatMoney(regularTotal)} separately · ${formatMoney(offerTotal)} after bundle savings · save ${formatMoney(bundleSavings)}. Checkout is disabled until pricing is live.`;
        } else {
          message.textContent = `${inCart ? `With the ${inCart} in your cart: ` : ''}${formatMoney(regularTotal)} separately · ${formatMoney(offerTotal)} after bundle savings · save ${formatMoney(bundleSavings)}.`;
        }
      }
    }

    root.addEventListener('click', async event => {
      const remove = event.target.closest('[data-bundle-remove]');
      if (remove) {
        const id = Number(remove.closest('[data-bundle-slot]')?.dataset.variantId);
        const card = cards.find(c => Number(c.dataset.variantId) === id);
        if (card) setCount(card, (counts.get(id) || 0) - 1);
        return;
      }

      const change = event.target.closest('[data-bundle-change]');

      if (change) {
        const card = change.closest('[data-bundle-card]');
        if (!card) return;

        const id = Number(card.dataset.variantId);
        const current = counts.get(id) || 0;
        setCount(card, current + Number(change.dataset.bundleChange || 0));
        return;
      }

      if (event.target.closest('[data-bundle-add]')) {
        if (addButton?.disabled || !totalSelected() || cartItems.length + totalSelected() < required) return;

        const items = Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));
        const theme = window.throwbackTheme || {};
        const tracked = theme.withSource && theme.sourceFor ? theme.withSource(items, theme.sourceFor(null, 'tacklebox-builder')) : items;
        if (!items.length) return;

        addButton.disabled = true;
        addButton.textContent = 'Adding…';
        if (message) { message.textContent = ''; delete message.dataset.tone; }

        try {
          const response = await fetch(window.throwbackTheme?.routes?.cartAdd || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: tracked })
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'Unable to add the bundle.');
          }

          // Clear the builder so a second click can't add the same baits again.
          const added = totalSelected();
          counts.clear();
          notice = { text: `Added ${added} ${added === 1 ? 'bait' : 'baits'} to your cart. Build another anytime.`, tone: 'success' };
          window.dispatchEvent(new CustomEvent('throwback:cart:refresh', { detail: { open: true } }));
          loadCart();
        } catch (error) {
          notice = { text: error.message || 'Unable to add the bundle. Please try again.', tone: 'error' };
        } finally {
          update();
        }
      }
    });

    window.addEventListener('throwback:cart:changed', event => syncCart(event.detail?.cart));
    window.addEventListener('pageshow', event => { if (event.persisted) loadCart(); });
    update();
    loadCart();
  });
})();
